/**
 * Deterministic fixed-step thunderstorm envelope for the yard.
 *
 * No Three, no Rapier, no scene, and no clock: this must be unit-testable in
 * Node exactly like worker.ts. Lightning is not a light. This module writes
 * `flash`; a four-line decorator in the yard director, not here, adds it to
 * LightGrid.sample. D-027 says this hardware is bound by lights per fragment,
 * and an exterior has no walls to cull behind.
 *
 * Anything reading grid.values directly bypasses the flash. Nothing does
 * today. Beat 6 gate 3 (exposure delta) in docs/beat-6-plan.md enforces that
 * decorator; this comment is not itself the gate (D-097). Math.random() is
 * forbidden: D-045 requires the simulation to replay.
 */

import { mulberry32 } from '@/audio/prng';

/**
 * Every authored feel number is kept here rather than hidden in equations.
 * The comments describe the player experience; implementation detail belongs
 * next to the relevant calculation.
 */
export const STORM = {
  /** The half-second warning that makes an irregular strike fair instead of arbitrary. */
  tell: 0.55,
  /** A brief leader flicker gives the player one readable cue, not a new rhythm to solve. */
  tellDuration: 0.09,
  /** The cue stays dim enough that it warns without already exposing the crossing. */
  tellLevel: 0.12,
  /** A short rise lets the sky feel violent while still landing on a fixed simulation step. */
  strikeRise: 0.06,
  /** The full white holds long enough for its danger to register before the sky falls away. */
  decayStart: 0.75,
  /** The long fall leaves the player judging cover, not reacting to a one-frame strobe. */
  decayDuration: 0.9,
  /** The return stroke reads as lightning texture without becoming a second exposure event. */
  secondaryBump: 0.35,
  /** The return stroke comes after the first fall has made the strike's shape legible. */
  secondaryAt: 0.28,
  /** A narrow return stroke gives the decay a scar rather than extending the flash. */
  secondaryWidth: 0.16,
  /** Less time than this would turn the crossing into a learnt metronome. */
  intervalMin: 7,
  /** More time than this and the player stops believing the weather has a rhythm. */
  intervalMax: 16,
  /** Overhead thunder follows close enough to bind sound to the visible strike. */
  thunderDelayMin: 1.5,
  /** Horizon thunder arrives late enough to make distance audible. */
  thunderDelayMax: 4,
} as const;

export interface StormOptions {
  /** Integer seed for mulberry32. Required. D-045: the simulation must replay. */
  readonly seed: number;
  /**
   * 0 = overhead (thunder 1.5 s after the strike), 1 = far horizon (4.0 s).
   * Defaults to 0.5. Authored by the room, not sampled.
   */
  readonly distance?: number;
}

export interface StormState {
  /** 0..1. The one number the light-grid decorator and the render step both read. */
  flash: number;
  /** True during the tell flicker window only. */
  tell: boolean;
  /** True on exactly the step thunder arrives. */
  thunder: boolean;
  /** True from tell onset until the envelope has fully decayed. */
  flashing: boolean;
}

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`storm ${label} must be finite`);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

/**
 * Below this, the decay tail is not a flash and is written as exactly 0.
 *
 * `#time` is accumulated one fixed step at a time, so the last step of an
 * envelope lands a few ulps short of `ENVELOPE_END` rather than on it — and the
 * decay then evaluates to about 1e-15 instead of zero. That is dust, but it
 * makes `flash === 0` an *approximate* guarantee, and three separate readers
 * take this number at face value: the light-grid decorator adds it to a sampled
 * exposure, the light rig scales the ambient term by it, and the post stack
 * lifts the whole frame by it. A guarantee that holds to fifteen decimal places
 * is the kind that gets tested with `toBeCloseTo` and then relied on with `> 0`.
 *
 * Caught by `stop() lets the in-flight envelope finish and then goes idle` in
 * `tests/e2e/storm.spec.ts`, which asserts `toBe(0)` on exactly that step — that
 * test is the gate, not this comment.
 */
const DARK = 1e-6;

function snapToDark(value: number): number {
  return value < DARK ? 0 : value;
}

/** The end is deliberately derived once so a later authored change cannot split the envelope and scheduler. */
const ENVELOPE_END = STORM.decayStart + STORM.decayDuration;

export class Storm {
  readonly #rng: () => number;
  readonly #distance: number;
  /** Kept as one object so every reader observes the fixed simulation's same state view. */
  readonly #state: StormState = { flash: 0, tell: false, thunder: false, flashing: false };
  #time = 0;
  #hasStarted = false;
  #scheduling = false;
  #tellAt: number | null = null;
  #nextTellAt: number | null = null;
  #thunderAt: number | null = null;

  constructor(options: StormOptions) {
    finite(options.seed, 'seed');
    if (options.distance !== undefined) finite(options.distance, 'distance');

    this.#rng = mulberry32(options.seed);
    this.#distance = clamp(options.distance ?? 0.5, 0, 1);
  }

  /**
   * Same-identity readonly view. Callers must not mutate. Retaining this
   * reference across step() observes the same object mutated in place, so a
   * naive before/after diff always reads as zero; snapshot its fields instead.
   */
  get state(): Readonly<StormState> {
    return this.#state;
  }

  /** Begin the irregular sequence. Calling again deliberately does not reset its replay. */
  start(): void {
    if (this.#hasStarted) return;
    this.#hasStarted = true;
    this.#scheduling = true;

    if (this.#tellAt !== null) {
      // A teach flash is already a real strike. Starting from wall-clock now
      // would shorten its next interval and make the authored lesson lie.
      this.#scheduleAfterTell(this.#tellAt);
    }
    // Leave idle scheduling to step(): this makes start(); trigger() and
    // trigger(); start() consume one matching RNG draw. The "start() and
    // trigger() commute their seeded schedule" test in storm.spec.ts enforces it.
    //
    // It used to sample here, and the second draw taken by a following
    // trigger() silently threw the first one away — same seed, same two calls,
    // different order, and every strike time for the rest of the run diverged.
    // Beat 6 makes both calls: stage 3 triggers the teach flash, stage 4 starts
    // the sequence. **The gate is proven**: putting the eager sample back turns
    // that one test red and no other (D-100).
  }

  /**
   * Stop new tells while preserving the live envelope and delayed sound.
   * Removing either would make the visual strike disagree with mechanics that
   * sampled its flash earlier in the same fixed simulation. The "stop()
   * preserves delayed thunder from an in-flight envelope" test in storm.spec.ts
   * enforces the sound half of that contract.
   */
  stop(): void {
    this.#scheduling = false;
    this.#nextTellAt = null;
  }

  /** Start the director's authored teach flash when the yard is otherwise dark. */
  trigger(): void {
    if (this.#tellAt !== null) return;
    this.#state.thunder = false;
    this.#beginEnvelope(this.#time);
    this.#writeEnvelope(this.#time);
  }

  /** Advance exactly one fixed simulation step. Returns the new state. */
  step(dt: number): Readonly<StormState> {
    if (!Number.isFinite(dt) || dt < 0) throw new Error('storm step dt must be finite and not negative');
    // The dt > STORM.tellDuration rejection is the observable-tell gate: a
    // state write can never pass its entire flicker. STORM.intervalMin is far
    // larger, so this same ceiling keeps two thunder edges from collapsing into
    // one step; the dt > STORM.tellDuration rejection itself enforces both rules.
    if (dt > STORM.tellDuration) throw new Error('storm step dt must not exceed tell duration');
    if (this.#scheduling && this.#nextTellAt === null && this.#tellAt === null) {
      this.#nextTellAt = this.#time + this.#sampleInterval() - STORM.tell;
    }
    // A zero-sized fixed step does not advance time or erase a thunder edge
    // another reader has not consumed; storm.spec.ts's "dt === 0 retains an
    // unconsumed thunder edge" test enforces the latter property.
    if (dt === 0) return this.#state;

    this.#state.thunder = false;
    const end = this.#time + dt;
    let cursor = this.#time;

    // Exact hand-offs retain internal scheduling across an event boundary. State
    // is written only at the end of this step; the dt <= STORM.tellDuration
    // ceiling above is what guarantees the tell remains observable to a caller.
    while (cursor < end) {
      let eventAt = end;
      let hasEvent = false;
      if (this.#thunderAt !== null && this.#thunderAt > cursor && this.#thunderAt <= end) {
        eventAt = Math.min(eventAt, this.#thunderAt);
        hasEvent = true;
      }
      if (this.#tellAt !== null) {
        const envelopeEnd = this.#tellAt + ENVELOPE_END;
        if (envelopeEnd > cursor && envelopeEnd <= end) {
          eventAt = Math.min(eventAt, envelopeEnd);
          hasEvent = true;
        }
      } else if (this.#nextTellAt !== null && this.#nextTellAt > cursor && this.#nextTellAt <= end) {
        eventAt = Math.min(eventAt, this.#nextTellAt);
        hasEvent = true;
      }

      if (!hasEvent) {
        cursor = end;
        break;
      }
      cursor = eventAt;

      if (this.#thunderAt === eventAt) {
        this.#state.thunder = true;
        this.#thunderAt = null;
      }
      if (this.#tellAt !== null && this.#tellAt + ENVELOPE_END === eventAt) this.#tellAt = null;
      if (this.#tellAt === null && this.#nextTellAt === eventAt) this.#beginEnvelope(eventAt);
    }

    this.#time = end;
    this.#writeEnvelope(end);
    return this.#state;
  }

  #sampleInterval(): number {
    return STORM.intervalMin + this.#rng() * (STORM.intervalMax - STORM.intervalMin);
  }

  #scheduleAfterTell(tellAt: number): void {
    // Tell onset is exactly `tell` before its strike, so subtracting and adding
    // it would only invite rounding drift in long deterministic recordings.
    this.#nextTellAt = tellAt + this.#sampleInterval();
  }

  #beginEnvelope(tellAt: number): void {
    this.#tellAt = tellAt;
    const thunderDelay = STORM.thunderDelayMin + this.#distance * (STORM.thunderDelayMax - STORM.thunderDelayMin);
    this.#thunderAt = tellAt + STORM.tell + thunderDelay;
    if (this.#scheduling) this.#scheduleAfterTell(tellAt);
  }

  #writeEnvelope(now: number): void {
    if (this.#tellAt === null) {
      this.#state.flash = 0;
      this.#state.tell = false;
      this.#state.flashing = false;
      return;
    }

    const t = now - this.#tellAt;
    this.#state.flashing = true;
    if (t <= STORM.tellDuration) {
      this.#state.flash = STORM.tellLevel;
      this.#state.tell = true;
    } else if (t < STORM.tell) {
      this.#state.flash = 0;
      this.#state.tell = false;
    } else if (t < STORM.tell + STORM.strikeRise) {
      this.#state.flash = (t - STORM.tell) / STORM.strikeRise;
      this.#state.tell = false;
    } else if (t < STORM.decayStart) {
      this.#state.flash = 1;
      this.#state.tell = false;
    } else {
      // step() clears #tellAt at its `tellAt + ENVELOPE_END` boundary before it
      // calls #writeEnvelope, so this active-envelope branch has t < ENVELOPE_END.
      // clamp(base + bump, 0, 1) would still read zero at that degenerate edge.
      const u = t - STORM.decayStart;
      const base = 1 - u / STORM.decayDuration;
      const x = 1 - Math.abs(u - STORM.secondaryAt) / STORM.secondaryWidth;
      const bump = x > 0 ? STORM.secondaryBump * x : 0;
      this.#state.flash = snapToDark(clamp(base + bump, 0, 1));
      this.#state.tell = false;
      // In the TAIL, and only here, `flashing` follows the light rather than the
      // bookkeeping. `#tellAt` is still set on the step where the decay reaches
      // dark — the boundary that clears it lands a few ulps later — so reporting
      // the envelope as in flight there would mean the sky is black and the
      // storm still says it is flashing. Everywhere earlier in the envelope the
      // two disagree on purpose: between the tell and the strike the flash is 0
      // by design and the storm IS in flight, which is why this is one branch
      // and not a rule.
      this.#state.flashing = this.#state.flash > 0;
    }
  }
}
